import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOnboardingState } from '../../hooks/useOnboardingState.jsx';
import { MONTHLY_PLANS, YEARLY_PLANS } from '@/data/utils/plansData';
import { getPlanType } from '../../utils/stepContentByPlan';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import PlanSwitcher from './plan-switcher';
import PricingCard from './pricing-card';
import PricingCarousel from './plans-carousel';
import Logo from '@/Asset/images/logo-blaxk.webp';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import Toast from '@/utils/toastManager';
import { trackPlansPageEvent } from '@/data/utils/plansPageAnalytics';
import './SelectPlan.css';

const DEFAULT_STUDIO_SEAT_ID = 'Studio-Account-5';
const STUDIO_SEAT_IDS = new Set(['Studio-Account-5', 'Studio-Account-10']);

const SelectPlan = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { state, updateState } = useOnboardingState();
	const [billingCycle, setBillingCycle] = useState('yearly');
	const [selectedPlanId, setSelectedPlanId] = useState(null);
	const initialStudioSeatId = STUDIO_SEAT_IDS.has(state.selectedPlanId)
		? state.selectedPlanId
		: DEFAULT_STUDIO_SEAT_ID;
	const [selectedStudioSeatByCycle, setSelectedStudioSeatByCycle] = useState({
		monthly: initialStudioSeatId,
		yearly: initialStudioSeatId
	});

	const plans = billingCycle === 'monthly' ? MONTHLY_PLANS : YEARLY_PLANS;
	const selectedStudioSeatId = selectedStudioSeatByCycle[billingCycle];

	const persistOnboardingState = updates => {
		updateState(updates);
		const currentState = JSON.parse(sessionStorage.getItem('onboarding_state') || '{}');
		sessionStorage.setItem(
			'onboarding_state',
			JSON.stringify({
				...currentState,
				...updates
			})
		);
	};

	const savePlanSelection = async ({ id, planType, cbItem, selectedCycle }) => {
		const onboardingPreferences = {
			...(state.onboardingPreferences || {}),
			selectedPlanId: id,
			selectedPlanType: planType,
			selectedBillingCycle: selectedCycle,
			selectedPlanCbItem: cbItem ?? null
		};

		await onboardingV2Api.updateOnboardingData({
			onboardingStep: 2,
			onboardingPreferences
		});

		return onboardingPreferences;
	};

	const handleSelectPlan = async ({ id, cbItem }) => {
		if (!id) {
			console.error('[SelectPlan] No plan ID provided');
			Toast.error('Invalid plan selection');
			return;
		}
		
		try {
			console.log('[SelectPlan] Button clicked with plan:', { id, cbItem });
			
			const planType = getPlanType(id);
			console.log('[SelectPlan] Plan type determined:', planType);
			
			setSelectedPlanId(id);

			// Track plan selection
			trackPlansPageEvent({
				eventName: 'onboarding_v2_plan_selected',
				planId: cbItem || id,
				billingCycle,
				source: 'onboarding_v2_select_plan',
				meta: { planType }
			});

			// Save to backend and sessionStorage
			const onboardingPreferences = await savePlanSelection({
				id,
				planType,
				cbItem,
				selectedCycle: billingCycle
			});

			// Save to sessionStorage immediately
			persistOnboardingState({
				selectedPlanId: id,
				selectedPlanType: planType,
				selectedPlanCbItem: cbItem ?? null,
				selectedBillingCycle: billingCycle,
				currentStep: 2,
				onboardingPreferences
			});
			console.log('[SelectPlan] State persisted');

			// Track plan selection with Meta Pixel
			window?.fbq?.('trackCustom', 'OnboardingV2_PlanSelected', {
				CustomEvent: 'OnboardingV2_PlanSelected',
				plan: id
			});

			// Navigate to checkout after state is saved
			const params = new URLSearchParams({
				...Object.fromEntries(new URLSearchParams(location.search).entries()),
				plan: planType,
				billing: billingCycle,
				planId: id
			});
			const checkoutUrl = `/onboarding/checkout?${params.toString()}`;
			console.log('[SelectPlan] Navigating to:', checkoutUrl);
			navigate(checkoutUrl);
			console.log('[SelectPlan] Navigation called');
		} catch (error) {
			console.error('[SelectPlan] Error in handleSelectPlan:', error);
			Toast.error('An error occurred while selecting the plan');
		}
	};

	const handleSeatChange = (seatId, plan) => {
		setSelectedStudioSeatByCycle(prev => ({
			...prev,
			[billingCycle]: seatId
		}));
		const selectedSeat = plan.seats.find(seat => seat.seatId === seatId);
		if (!selectedSeat) return;
		const planType = getPlanType(seatId);
		setSelectedPlanId(seatId);

		persistOnboardingState({
			selectedPlanId: seatId,
			selectedPlanType: planType,
			selectedPlanCbItem: selectedSeat.cbItem ?? null,
			selectedBillingCycle: billingCycle,
			currentStep: 2
		});
	};

	const handleBillingCycleChange = cycle => {
		trackPlansPageEvent({
			eventName: 'onboarding_v2_billing_cycle_changed',
			billingCycle: cycle,
			source: 'onboarding_v2_select_plan',
			meta: { previousCycle: billingCycle }
		});
		setBillingCycle(cycle);
		persistOnboardingState({
			selectedBillingCycle: cycle
		});
	};

	return (
		<div className="onboarding-select-plan">
			<div className="onboarding-select-plan__grid-overlay" />

			<header className="onboarding-select-plan__header">
				<div className="onboarding-select-plan__brand-row">
					<button
						type="button"
						className="onboarding-select-plan__back-btn"
						onClick={() => navigate(`/onboarding/create-profile${location.search}`)}
					>
						<KeyboardBackspaceOutlinedIcon />
					</button>
					<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-select-plan__logo" />
				</div>
				<div className="onboarding-select-plan__title-row">
					<div>
						<h1 className="onboarding-select-plan__title">Select Your Plan</h1>
						<p className="onboarding-select-plan__subtitle">Choose the level of access that fits your goals.</p>
					</div>

					<PlanSwitcher billingCycle={billingCycle} onChange={handleBillingCycleChange} />
				</div>
			</header>

			<section className="onboarding-select-plan__cards onboarding-select-plan__cards--desktop">
				{plans.map((plan, index) => (
					<PricingCard
						key={plan.id}
						plan={plan}
						billingCycle={billingCycle}
						isMostPopular={index === 1}
						isSelected={selectedPlanId === (plan.hasSeatSelection ? selectedStudioSeatId : plan.id)}
						selectedSeatId={selectedStudioSeatId}
						onSeatChange={seatId => handleSeatChange(seatId, plan)}
						onSelectPlan={handleSelectPlan}
					/>
				))}
			</section>

			<section className="onboarding-select-plan__cards onboarding-select-plan__cards--mobile">
				<PricingCarousel
					plans={plans}
					billingCycle={billingCycle}
					selectedPlanId={selectedPlanId}
					selectedStudioSeatId={selectedStudioSeatId}
					onSeatChange={handleSeatChange}
					onSelectPlan={handleSelectPlan}
				/>
			</section>
		</div>
	);
};

export default SelectPlan;
